import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { FaPlus, FaArrowLeft, FaLock, FaCheckCircle } from 'react-icons/fa';

const CATEGORIES = ['Food', 'Salary', 'Utilities', 'Electronics', 'Health', 'Transport', 'Housing', 'Entertainment', 'Healthcare', 'Shopping', 'Investment', 'Other'];

const categoryEmoji = {
  Food: '🍔', Salary: '💼', Utilities: '⚡', Electronics: '💻',
  Health: '💪', Transport: '🚗', Housing: '🏠', Entertainment: '🎬',
  Healthcare: '🏥', Shopping: '🛍️', Investment: '📈', Other: '📁',
};

const AddTransaction = () => {
  const { addTransaction, role, formatCurrency, currency } = useApp();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: '', amount: '', category: 'Food', type: 'expense',
    date: new Date().toISOString().split('T')[0],
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  if (role !== 'admin') {
    return (
      <div className="fade-in" style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '60vh', gap: '1.5rem',
        textAlign: 'center',
      }}>
        {/* Lock card */}
        <div className="card" style={{
          maxWidth: '380px', width: '100%', padding: '3rem 2rem',
          textAlign: 'center', position: 'relative', overflow: 'hidden',
        }}>
          {/* Background glow */}
          <div style={{
            position: 'absolute', top: '-60px', left: '50%', transform: 'translateX(-50%)',
            width: '200px', height: '200px', borderRadius: '50%',
            background: 'radial-gradient(circle, var(--danger-light) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          <div style={{
            width: '80px', height: '80px', borderRadius: '24px',
            background: 'var(--danger-light)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(248,113,113,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 1.5rem',
            boxShadow: '0 8px 32px rgba(248,113,113,0.2)',
          }}>
            <FaLock size={30} color="var(--danger)" />
          </div>

          <h2 style={{ fontSize: '1.5rem', fontWeight: 900, color: 'var(--text-primary)', marginBottom: '0.75rem', letterSpacing: '-0.02em' }}>
            Access Restricted
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.65, marginBottom: '2rem' }}>
            Only <strong>Admins</strong> can add transactions.<br />
            Switch your role using the navbar to continue.
          </p>
          <button onClick={() => navigate('/')} className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center' }}>
            <FaArrowLeft size={13} /> Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Title is required';
    if (!form.amount || isNaN(form.amount) || parseFloat(form.amount) <= 0) e.amount = 'Enter a valid positive amount';
    if (!form.date) e.date = 'Date is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    addTransaction(form);
    setSuccess(true);
    setTimeout(() => navigate('/transactions'), 1400);
  };

  const handleChange = (field, value) => {
    setForm(p => ({ ...p, [field]: value }));
    if (errors[field]) setErrors(p => ({ ...p, [field]: '' }));
  };

  const hasPreview = form.title.trim() && form.amount && parseFloat(form.amount) > 0;

  return (
    <div className="fade-in" style={{ maxWidth: '580px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <button onClick={() => navigate(-1)} className="btn btn-ghost btn-sm" style={{ marginBottom: '1.25rem' }}>
          <FaArrowLeft size={11} /> Back
        </button>
        <h1 className="page-title">Add Transaction</h1>
        <p className="page-subtitle">Record a new income or expense entry</p>
      </div>

      {/* Type Toggle */}
      <div className="card" style={{ marginBottom: '1.25rem', padding: '0.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          {['expense', 'income'].map(type => (
            <button
              key={type}
              type="button"
              onClick={() => handleChange('type', type)}
              style={{
                padding: '0.85rem 1rem',
                borderRadius: '14px',
                border: form.type === type
                  ? type === 'income' ? '1px solid rgba(52,211,153,0.4)' : '1px solid rgba(248,113,113,0.4)'
                  : '1px solid transparent',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '0.92rem',
                transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
                fontFamily: 'Inter, sans-serif',
                letterSpacing: '-0.01em',
                background: form.type === type
                  ? type === 'income'
                    ? 'linear-gradient(135deg, rgba(5,150,105,0.9), rgba(52,211,153,0.85))'
                    : 'linear-gradient(135deg, rgba(220,38,38,0.9), rgba(248,113,113,0.85))'
                  : 'transparent',
                color: form.type === type ? 'white' : 'var(--text-muted)',
                boxShadow: form.type === type
                  ? type === 'income' ? '0 4px 20px rgba(16,185,129,0.35)' : '0 4px 20px rgba(239,68,68,0.35)'
                  : 'none',
              }}
            >
              {type === 'income' ? '📈 Income' : '📉 Expense'}
            </button>
          ))}
        </div>
      </div>

      {/* Form Card */}
      <form onSubmit={handleSubmit} className="card" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

        {/* Title */}
        <div>
          <label className="form-label" htmlFor="tx-title">Description</label>
          <input
            id="tx-title"
            type="text"
            className="input-field"
            placeholder="e.g. Monthly Salary, Grocery run..."
            value={form.title}
            onChange={e => handleChange('title', e.target.value)}
            style={errors.title ? { borderColor: 'var(--danger)', boxShadow: '0 0 0 3px var(--danger-light)' } : {}}
          />
          {errors.title && <p style={{ color: 'var(--danger)', fontSize: '0.78rem', marginTop: '0.4rem', fontWeight: 600 }}>⚠ {errors.title}</p>}
        </div>

        {/* Amount */}
        <div>
          <label className="form-label" htmlFor="tx-amount">Amount ({currency.code})</label>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)',
              color: 'var(--text-muted)', fontWeight: 700, fontSize: '0.9rem',
              pointerEvents: 'none',
            }}>{currency.symbol}</span>
            <input
              id="tx-amount"
              type="number"
              step="0.01"
              min="0"
              className="input-field"
              placeholder="0.00"
              value={form.amount}
              onChange={e => handleChange('amount', e.target.value)}
              style={{
                paddingLeft: '2rem',
                ...(errors.amount ? { borderColor: 'var(--danger)', boxShadow: '0 0 0 3px var(--danger-light)' } : {}),
              }}
            />
          </div>
          {errors.amount && <p style={{ color: 'var(--danger)', fontSize: '0.78rem', marginTop: '0.4rem', fontWeight: 600 }}>⚠ {errors.amount}</p>}
        </div>

        {/* Category */}
        <div>
          <label className="form-label" htmlFor="tx-category">Category</label>
          <select
            id="tx-category"
            className="input-field"
            value={form.category}
            onChange={e => handleChange('category', e.target.value)}
          >
            {CATEGORIES.map(c => <option key={c} value={c}>{categoryEmoji[c]} {c}</option>)}
          </select>
        </div>

        {/* Date */}
        <div>
          <label className="form-label" htmlFor="tx-date">Date</label>
          <input
            id="tx-date"
            type="date"
            className="input-field"
            value={form.date}
            onChange={e => handleChange('date', e.target.value)}
            style={errors.date ? { borderColor: 'var(--danger)', boxShadow: '0 0 0 3px var(--danger-light)' } : {}}
          />
          {errors.date && <p style={{ color: 'var(--danger)', fontSize: '0.78rem', marginTop: '0.4rem', fontWeight: 600 }}>⚠ {errors.date}</p>}
        </div>

        {/* Live Preview */}
        {hasPreview && (
          <div className="scale-in" style={{
            padding: '1.1rem 1.25rem',
            borderRadius: '14px',
            background: form.type === 'income' ? 'var(--success-light)' : 'var(--danger-light)',
            backdropFilter: 'blur(8px)',
            border: `1px solid ${form.type === 'income' ? 'rgba(52,211,153,0.3)' : 'rgba(248,113,113,0.3)'}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{
                width: '40px', height: '40px', borderRadius: '12px',
                background: form.type === 'income' ? 'rgba(52,211,153,0.2)' : 'rgba(248,113,113,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem',
              }}>{categoryEmoji[form.category] || '📁'}</span>
              <div>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.15rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Preview</p>
                <p style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.9rem' }}>{form.title}</p>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{form.category} · {form.date}</p>
              </div>
            </div>
            <span style={{
              fontWeight: 900, fontSize: '1.35rem', letterSpacing: '-0.02em',
              color: form.type === 'income' ? 'var(--success)' : 'var(--danger)',
              whiteSpace: 'nowrap',
            }}>
              {form.type === 'income' ? '+' : '-'}{formatCurrency(parseFloat(form.amount || 0))}
            </span>
          </div>
        )}

        {/* Divider */}
        <div className="glow-divider" />

        {/* Submit */}
        <button
          type="submit"
          className={`btn ${form.type === 'income' ? 'btn-success' : 'btn-primary'}`}
          style={{
            width: '100%',
            justifyContent: 'center',
            padding: '0.95rem',
            fontSize: '0.95rem',
            fontWeight: 800,
            letterSpacing: '-0.01em',
            opacity: success ? 0.85 : 1,
          }}
          disabled={success}
        >
          {success ? (
            <><FaCheckCircle size={16} /> Added Successfully!</>
          ) : (
            <><FaPlus size={14} /> Add {form.type === 'income' ? 'Income' : 'Expense'}</>
          )}
        </button>
      </form>
    </div>
  );
};

export default AddTransaction;
